// Copyright 2016 Sorama B.V.

import { Point3f } from "datatypes/SoramaLocalApi";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";

export interface IHardwareReserver<TKey> {
    onDeviceLost(device: TKey): void;
}

export abstract class IReservableHardwareService<
    THardware extends IHardwareDevice,
    TKey,
> extends IHardwareService<THardware> {
    /**
     * Checks if the hardware configuration can be reserved. Throws an error with reason if it can not.
     * @param devices The hardware configuration to check
     */
    abstract canReserve(devices: THardware[]): Promise<void>;

    /**
     * Reserve the hardware configuration for use. Returns a reservation key which can be used for future operations on the reserved device.
     * @param hardware The hardware configuration to reserve
     * @param reserver A reserver
     */
    abstract reserve(
        hardwareConfiguration: THardware[],
        reserver: IHardwareReserver<TKey>,
        positions?: Point3f[]
    ): Promise<TKey>;

    /**
     * Release the hardware device.
     * @param reservationKey The reservation key obtained when reserving the hardware configuration
     * @param reserver A reserver
     */
    abstract release(reservationKey: TKey, reserver: IHardwareReserver<TKey>): Promise<void>;
}
